// Core modules
import { HTTP_INTERCEPTORS, HttpClient, HttpClientModule } from '@angular/common/http';
import { NgModule, APP_INITIALIZER, ErrorHandler } from '@angular/core';
import { Router } from '@angular/router';
import { BrowserModule } from '@angular/platform-browser';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';

// External modules
import { LeafletModule } from '@asymmetrik/ngx-leaflet';
import { StoreModule } from '@ngrx/store';
import { StoreDevtoolsModule } from '@ngrx/store-devtools';

// App modules
import { HomeModule } from 'src/app/home/home.module';
import { CoreModule } from '@app-core/core.module';
import { SharedModule } from '@app-shared/shared.module';
import { AppRoutingModule } from './app-routing.module';
import { AuthenticationModule } from '@app-auth/authentication.module';
import { IframeAuthenticationModule } from './iframe-authentication/iframe-authentication.module';

// components
import { AppComponent, AppGeotabComponent } from './app.component';

import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { ServiceWorkerModule } from '@angular/service-worker';

import { environment } from '@env/environment';
import { metaReducers } from './reducers';
import { TranslateHttpLoader } from '@ngx-translate/http-loader';
import { TranslateLoader, TranslateModule } from '@ngx-translate/core';

import * as Sentry from '@sentry/angular';
import { HttpInterceptorService } from '@app-core/interceptors/httpInterceptor.service';

import { getClientConfig } from '@config/client-config';
import { AuthGuard } from '@app-core/guards/auth/auth.guard';
import { EulaConsentGuard } from '@app-core/guards/eula-consent/eula-consent.guard';
import { DynamicGuardsService } from '@app-core/services/dynamic-guards/dynamic-guards.service';
import { setupGeotabRouter } from './geotab/geotab-router';
import { GeotabAuthGuard } from '@app-core/guards/geotab-auth/geotab-auth.guard';
import { DoBootstrap, ApplicationRef } from '@angular/core';

// AoT requires an exported function for factories
export function HttpLoaderFactory(http: HttpClient) {
  return new TranslateHttpLoader(http, 'assets/i18n/', '.json');
}

export function setupRouter(router: Router, guardStore: DynamicGuardsService) {
  return () => {
    const clientConfig = getClientConfig().configuration;
    const env = getClientConfig().environments;
    const ROUTES_WITHOUT_GUARDS = ['notifications-page'];

    if (env.isGeotabEnv) {
      setupGeotabRouter();
    }

    // Decide wildcard route from config.json
    const wildcardRoute = clientConfig?.wildcardRoute;

    const canActivateGuards = Boolean(env.isGeotabEnv) ? [GeotabAuthGuard] : [AuthGuard, EulaConsentGuard];

    guardStore.setGuards(canActivateGuards);

    const updatedRoutes = router.config.map((route) => {
      if (route.loadChildren) {
        if (ROUTES_WITHOUT_GUARDS.includes(route.path!)) {
          return { ...route };
        }
        return {
          ...route,
          canActivate: canActivateGuards,
        };
      }
      return route;
    });

    updatedRoutes.push({ path: '', redirectTo: wildcardRoute, pathMatch: 'full' }, { path: '**', redirectTo: wildcardRoute });

    router.resetConfig(updatedRoutes);
  };
}

export function sentryErrorHandlerFactory() {
  const clientConfig = getClientConfig().configuration;
  const env = getClientConfig().environments;
  const disable = clientConfig.disableSentry;
  const isGeotab = env?.isGeotabEnv || false;

  if (disable) {
    // Return a default ErrorHandler (logs to console) when Sentry is disabled
    return new ErrorHandler();
  }

  return Sentry.createErrorHandler({
    showDialog: false,
    logErrors: !isGeotab,
  });
}

export function sentryTraceServiceFactory(router: Router) {
  const clientConfig = getClientConfig().configuration;
  if (clientConfig.disableSentry) return null;
  return new Sentry.TraceService(router);
}

@NgModule({
  declarations: [AppComponent, AppGeotabComponent],
  imports: [
    BrowserModule,
    BrowserAnimationsModule,
    HttpClientModule,
    LeafletModule.forRoot(),
    StoreModule.forRoot({}, { metaReducers }),
    MatProgressSpinnerModule,
    !environment.production ? StoreDevtoolsModule.instrument() : [],
    TranslateModule.forRoot({
      loader: {
        provide: TranslateLoader,
        useFactory: HttpLoaderFactory,
        deps: [HttpClient],
      },
    }),
    CoreModule,
    SharedModule,
    AuthenticationModule,
    IframeAuthenticationModule,
    HomeModule,
    AppRoutingModule,
    ServiceWorkerModule.register('ngsw-worker.js', { enabled: environment.production }),
  ],
  providers: [
    {
      provide: APP_INITIALIZER,
      useFactory: setupRouter,
      deps: [Router, DynamicGuardsService],
      multi: true,
    },
    {
      provide: ErrorHandler,
      useFactory: sentryErrorHandlerFactory,
    },
    {
      provide: Sentry.TraceService,
      useFactory: sentryTraceServiceFactory,
      deps: [Router],
    },
    {
      provide: HTTP_INTERCEPTORS,
      useClass: HttpInterceptorService,
      multi: true,
    },
  ],
})
export class AppModule implements DoBootstrap {
  ngDoBootstrap(appRef: ApplicationRef): void {
    const config = getClientConfig();

    if (config?.environments?.isGeotabEnv) {
      appRef.bootstrap(AppGeotabComponent);
    } else {
      appRef.bootstrap(AppComponent);
    }
  }
}
